'use client';
import React, { useEffect, useState } from 'react';

import Link from 'next/link';
import { useTranslations } from 'next-intl';

import PaymentForm from 'components/payment/components/PaymentForm';
import useTenant from 'hooks/useTenant';
import { PaymentElementLanguageType } from 'types/checkout';
import { APIDataResponseType } from 'types/common';
import { ProductPackageDetailsDataType } from 'types/productPackage';
import { PlanItemType } from 'types/subscriptionPlans';
import ImgixImage from 'ui/ImgixImage';
import RichTextEditorContentRender from 'ui/RichTextEditorContentRender';
import { MemberPersonalInfo, ProfileAddress } from 'utils/GeneratedApi';
import { isMatchedIntroAndRecurringPricePeriod } from 'utils/isMatchedIntroAndRecurringPricePeriod';
import removeHttps from 'utils/removeHttps';
import getFormattedDate from 'utils/getFormattedDate';

const Payment = ({
  packageDetailsResponse,
  stripePublishableKey,
  memberProfileData,
  memberAddressData,
  articleRef,
  onCheckoutCompletedUrl,
  checkoutStateUuid,
  callBackUrl,
  locale,
}: {
  packageDetailsResponse: ProductPackageDetailsDataType & APIDataResponseType;
  locale: PaymentElementLanguageType;
  stripePublishableKey: string;
  memberProfileData: MemberPersonalInfo;
  memberAddressData: ProfileAddress;
  articleRef?: string;
  onCheckoutCompletedUrl?: string;
  checkoutStateUuid: string;
  callBackUrl: string;
}) => {
  const tCheckout = useTranslations('checkout');
  const tSubscriptionPlans = useTranslations('subscriptionPlans');
  const tenant = useTenant();

  const [introPrice, setIntroPrice] = useState<number>(0);
  const [totalRecurringPrice, setTotalRecurringPrice] = useState<number>(0);

  useEffect(() => {
    setIntroPrice(packageDetailsResponse.price ?? 0);
    setTotalRecurringPrice(packageDetailsResponse.recurringPrice ?? 0);
  }, [packageDetailsResponse.price, packageDetailsResponse.recurringPrice]);

  if (packageDetailsResponse?.messageCode === 'notAvailableViaSlug') {
    return (
      <div className="flex items-center">
        <div>
          <span className="text-2xl font-bold text-black">{tCheckout('notAvailableViaSlug')}</span>
          {tenant?.siteUrl && (
            <p className="text-base text-darkGray">
              {tCheckout('notAvailableViaSlugHelpTextPrefix')}
              &nbsp;
              <Link href={tenant.siteUrl} className="underline font-bold text-gray-500">
                {removeHttps(tenant.siteUrl)}
              </Link>
              &nbsp;
              {tCheckout('notAvailableViaSlugHelpTextSuffix')}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (packageDetailsResponse?.messageCode === 'campaignIsOver') {
    return (
      <div className="flex items-center">
        <div>
          <span className="text-2xl font-bold text-black">{tCheckout('notAvailable')}</span>
          <p className="text-base text-darkGray">{tCheckout('campaignIsOver')}</p>
        </div>
      </div>
    );
  }

  if (!packageDetailsResponse || packageDetailsResponse.status !== 'published') {
    return (
      <div className="flex items-center">
        <div>
          <span className="text-2xl font-bold text-black">{tCheckout('notAvailable')}</span>
          <p className="text-base text-darkGray">{tCheckout('notAvailableHelpText')}</p>
        </div>
      </div>
    );
  }

  const {
    image,
    name,
    billingPeriod: introductionBillingPeriod,
    billingPeriodFrequency: introductionBillingFrequency,
    billingPeriodUnit: introductionBillingUnit,
    subscriptionPlan,
    description,
  } = packageDetailsResponse;

  const {
    autoRenew,
    billingPeriod: recurringBillingPeriod,

    billingPeriodFrequency: recurringBillingFrequency,
    billingPeriodUnit: recurringBillingUnit,
  } = subscriptionPlan || ({} as PlanItemType);

  const isPricePeriodMatched = isMatchedIntroAndRecurringPricePeriod(
    introPrice,
    totalRecurringPrice,
    introductionBillingPeriod,
    recurringBillingPeriod,
    introductionBillingFrequency,
    recurringBillingFrequency as number,
    introductionBillingUnit,
    recurringBillingUnit,
  );

  const getUnitText = (billingFrequency: number, billingUnit: string) => {
    if (billingFrequency === 1) {
      return tSubscriptionPlans(billingUnit).toLocaleLowerCase();
    }
    return tSubscriptionPlans(billingUnit + 's').toLocaleLowerCase();
  };

  const getPeriodText = (billingPeriod: string, billingFrequency: number, billingUnit: string) => {
    if (billingPeriod === 'custom') {
      if (billingUnit === 'year') {
        return tCheckout('customPeriodOneYear', {
          frequency: billingFrequency,
          unit: tSubscriptionPlans(billingUnit),
        });
      }

      return tCheckout('customPeriod', {
        frequency: billingFrequency,
        unit: billingUnit ? getUnitText(billingFrequency, billingUnit) : billingUnit,
      });
    } else if (billingPeriod) {
      return tCheckout(billingPeriod);
    }
    return '';
  };

  const introPeriodText = packageDetailsResponse.introPeriodFixedEndDate
    ? tCheckout('introPeriodWillEndOn', {
        date: getFormattedDate(new Date(packageDetailsResponse.introPeriodFixedEndDate), locale),
      })
    : tCheckout('introductionPeriodTextOnOff', {
        period: getPeriodText(
          introductionBillingPeriod,
          introductionBillingFrequency,
          introductionBillingUnit,
        ),
      });

  const introductionPeriodText =
    isPricePeriodMatched && autoRenew
      ? tCheckout('introductionPeriodTextRecurring', {
          period: getPeriodText(
            introductionBillingPeriod,
            introductionBillingFrequency,
            introductionBillingUnit,
          ),
        })
      : introPeriodText;

  const recurringPeriodText =
    isPricePeriodMatched || !autoRenew
      ? ''
      : tCheckout('recurringPeriodText', {
          price: packageDetailsResponse.recurringPrice ?? '',
          period: getPeriodText(
            recurringBillingPeriod as string,
            recurringBillingFrequency as number,
            recurringBillingUnit as string,
          ),
        });

  const handlePriceChange = (introPrice: number, recurringPrice: number) => {
    setIntroPrice(introPrice);
    setTotalRecurringPrice(recurringPrice);
  };

  return (
    <div className="bg-white py-6 sm:py-7">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto grid max-w-2xl grid-cols-1 items-start gap-x-8 gap-y-2 sm:gap-y-24 lg:mx-0 lg:max-w-none lg:grid-cols-2">
          <div className="flex flex-col gap-2 sm:px-8">
            {image && (
              <ImgixImage
                src={image}
                alt="package-image"
                options={{ h: 400, w: 300, fit: 'max' }}
                width={300}
                height={400}
                classStack="object-contain"
                loadingType="eager"
              />
            )}
            <h2 className="text-black text-[26px] leading-10 font-bold">{name}</h2>
            <h1 className="text-black text-[42px] leading-[64px] font-bold">
              {tCheckout('introductionPrice', {
                price: packageDetailsResponse.price,
              })}
            </h1>
            <span className="text-darkGray font-semibold text-base">{introductionPeriodText}</span>
            {recurringPeriodText && (
              <span className="text-darkGray font-semibold text-base">{recurringPeriodText}</span>
            )}
            {description && (
              <div className="mt-2">
                <RichTextEditorContentRender text={description} />
              </div>
            )}
          </div>
          <PaymentForm
            packageDetails={packageDetailsResponse}
            introPrice={introPrice}
            recurringPrice={totalRecurringPrice}
            checkoutStateUuid={checkoutStateUuid}
            articleRef={articleRef}
            onCheckoutCompletedUrl={onCheckoutCompletedUrl}
            stripePublishableKey={stripePublishableKey}
            memberProfileData={memberProfileData}
            memberAddressData={memberAddressData}
            callBackUrl={callBackUrl}
            handlePriceChange={handlePriceChange}
          />
        </div>
      </div>
    </div>
  );
};

export default Payment;
